import { useEffect, useMemo, useRef, useState } from 'react';
import { classifyPick, documentProblem, documentReady, titleFromFileName } from '@/onboarding/pickContent';
// The PURE splitter — the same one the server runs. Reaching for it through `textAsset.ts`
// would drag Cognito, and React Native, into the wizard.
import { previewParts } from '@/webapp/documentSplit';
import { useDraft } from '../../state/draft';
import { Button } from '../../ui';

/**
 * Bring your own content, and be told honestly what will happen to it.
 *
 * Ported from src/components/build/ContentStep.tsx. Only the picking differs: a browser
 * `File` hands over its text directly, so none of `pickContent()`'s React Native machinery
 * is needed — but every DECISION still comes from the shared pure functions, because those
 * are the tested ones and divergence between the two clients is the thing to avoid.
 *
 * A document is the app's SUBJECT: stored beside the app, split into parts, and read at
 * runtime. The model never sees the text, so it cannot paraphrase somebody's book.
 *
 * The rule that governs this screen: a document is only handed to the build once it says
 * where it came from. The server refuses a text with no attribution, and inventing one would
 * defeat the reason it is required — so the step holds the document back and says why,
 * instead of accepting the file and failing at publish.
 */
export default function ContentStep() {
  const attachedDoc = useDraft((s) => s.attachedDoc);
  const attachedMedia = useDraft((s) => s.attachedMedia);
  const setAttachedDoc = useDraft((s) => s.setAttachedDoc);
  const setAttachedMedia = useDraft((s) => s.setAttachedMedia);

  // The picked text lives here, not in the store, until it is complete enough to use.
  const [text, setText] = useState<string | null>(attachedDoc?.text ?? null);
  const [fileName, setFileName] = useState('');
  const [title, setTitle] = useState(attachedDoc?.title ?? '');
  const [attribution, setAttribution] = useState(attachedDoc?.attribution ?? '');
  const [headingPattern, setHeadingPattern] = useState(attachedDoc?.headingPattern ?? '');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  // The rule lives in a pure, tested function rather than here: this is the decision that
  // governs whether the document is used at all.
  const ready = documentReady(text ? { title, attribution, text } : null);

  // Only a COMPLETE document reaches the build. An incomplete one is not half-attached; it
  // is not attached, which is why this clears the store rather than leaving a stale value.
  useEffect(() => {
    if (ready && text) {
      setAttachedDoc({
        title: title.trim(),
        attribution: attribution.trim(),
        text,
        headingPattern: headingPattern.trim(),
      });
    } else {
      setAttachedDoc(null);
    }
  }, [ready, text, title, attribution, headingPattern, setAttachedDoc]);

  // What the split will actually produce — the server's own splitter, so the number shown
  // here is the number of parts that will exist.
  const partCount = useMemo(
    () => (text ? previewParts(text, headingPattern.trim() || undefined).length : 0),
    [text, headingPattern],
  );

  async function attach(file: File): Promise<void> {
    setBusy(true);
    setProblem(null);
    try {
      // Judged before it is read, by the same function the phone uses — so a PDF is refused
      // HERE with a sentence naming what to do instead, rather than being carried through a
      // build that will never mention it.
      const verdict = classifyPick(file.name, file.type);
      if (verdict.kind === 'unsupported') {
        setProblem(verdict.reason);
        return;
      }
      if (verdict.kind === 'media') {
        // Same file twice is a replace, not a duplicate — the name is the app's handle on it.
        const current = useDraft.getState().attachedMedia.filter((m) => m.name !== file.name);
        setAttachedMedia([...current, { name: file.name, contentType: verdict.contentType, file }]);
        return;
      }

      let content: string;
      try {
        content = await file.text();
      } catch {
        setProblem('That file could not be read. Try attaching it again.');
        return;
      }
      // Bytes, from the file itself — the character count is not the bound, the encoded
      // size is, and UTF-8 Arabic or CJK is 2-3 bytes a character.
      const refusal = documentProblem(content, file.size);
      if (refusal) {
        setProblem(refusal);
        return;
      }

      setText(content);
      setFileName(file.name);
      // A NEW document takes the new file's title; keeping the old one would build the app
      // around this text under the previous file's name. Attribution is left alone — a second
      // file from the same source usually has the same one.
      setTitle(titleFromFileName(file.name));
    } finally {
      setBusy(false);
      // So the same file can be picked again after being removed.
      if (input.current) input.current.value = '';
    }
  }

  function removeDocument(): void {
    setText(null);
    setFileName('');
    setTitle('');
    setAttribution('');
    setHeadingPattern('');
    setProblem(null);
  }

  return (
    <div className="content-step">
      <p className="type-body plan-detail">
        Optional. Attach something of your own and the app is built around it — a text is
        stored beside the app and read a part at a time, never rewritten by the model.
      </p>

      <input
        ref={input}
        id="attach-file"
        type="file"
        className="visually-hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void attach(file);
        }}
      />
      <Button
        title={busy ? 'Reading…' : 'Attach a file'}
        variant="secondary"
        loading={busy}
        onPress={() => input.current?.click()}
        accessibilityLabel="Attach a document, photo, audio or video"
      />

      {problem && (
        <p role="alert" className="type-caption field-error-text">{problem}</p>
      )}

      {text && (
        <div className="card clay-card content-card">
          <div className="content-card-head">
            <h3 className="type-heading">{fileName || 'Your document'}</h3>
            <button type="button" className="chip-button" onClick={removeDocument}>Remove</button>
          </div>

          <label className="type-caption field-label" htmlFor="doc-title">Title</label>
          <input
            id="doc-title"
            className="field"
            value={title}
            placeholder="The Qur'an"
            onChange={(e) => setTitle(e.target.value)}
          />

          <label className="type-caption field-label" htmlFor="doc-attribution">Where it came from</label>
          <input
            id="doc-attribution"
            className="field"
            value={attribution}
            placeholder="Public domain translation, named"
            onChange={(e) => setAttribution(e.target.value)}
          />
          {!attribution.trim() && (
            <p className="type-caption field-error-text">
              Needed — a translation is somebody’s work, and it is shown to readers.
            </p>
          )}

          <label className="type-caption field-label" htmlFor="doc-pattern">Split on (optional)</label>
          <input
            id="doc-pattern"
            className="field"
            value={headingPattern}
            placeholder="Sura \d+"
            onChange={(e) => setHeadingPattern(e.target.value)}
          />

          {/* The real number, from the real splitter — not an estimate. */}
          <p className="type-caption plan-detail">
            {partCount === 1
              ? 'Splits into 1 part. Add a pattern above to break it up.'
              : `Splits into ${partCount} parts.`}
          </p>
          {!ready && (
            <p className="type-caption plan-detail">
              Not attached yet — fill in the title and where it came from.
            </p>
          )}
        </div>
      )}

      {attachedMedia.length > 0 && (
        <div className="card clay-card content-card">
          <h3 className="type-heading">
            {attachedMedia.length} file{attachedMedia.length === 1 ? '' : 's'} attached
          </h3>
          <ul className="plan-review-list">
            {/* The NAME is what the app will be written against, so it is the thing to show. */}
            {attachedMedia.map((m) => (
              <li key={m.name} className="content-media-row">
                <span className="type-body">{m.name}</span>
                <button
                  type="button"
                  className="chip-button"
                  aria-label={`Remove ${m.name}`}
                  onClick={() => setAttachedMedia(attachedMedia.filter((x) => x.name !== m.name))}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
          <p className="type-caption plan-detail">
            The app will be built to use these by name, and they are uploaded when it publishes.
          </p>
        </div>
      )}
    </div>
  );
}
